import { useCallback, useEffect, useRef, useState } from "react";
import { useDataChannel, useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";

import {
    TOOL_BOARD_TOPIC,
    type PatientToolBoard,
    type TherapistToolBoard,
    type ToolBoardMessage,
    mayControlToolBoard,
    parseToolBoardMessage,
    patientFrameSrc,
} from "../domain/meetingToolBoard";
import { leaveBoardFullscreen } from "./boardFullscreen";

const encoder = new TextEncoder();

type DataSend = (payload: Uint8Array, options: object) => Promise<void> | void;

export type ToolBoardChannel = {
    /** Whether this side puts boards up for the room. */
    canControl: boolean;
    /** The page this screen frames while a board is up, or null for the faces. */
    frameSrc: string | null;
    kind: TherapistToolBoard["kind"] | null;
    /**
     * Back to the faces on this screen only — the patient's, when their board
     * says it has been finished before the therapist's "close" arrives.
     */
    dismiss: () => void;
};

/**
 * A tool's board, put up for the whole call over its LiveKit data channel.
 *
 * The therapist's board comes from the page (the tools panel that opens it
 * sits outside the room), and this sends its patient half to everyone else —
 * again whenever somebody joins late and asks. The patient's comes from the
 * packets, obeyed only from an identity that may send them.
 *
 * Call it above whatever draws the board, so the faces coming back for a
 * moment do not lose which board was up.
 */
export function useToolBoardChannel(therapistBoard: TherapistToolBoard | null): ToolBoardChannel {
    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    /*
     * Read live from the room wherever a packet is handled: the identity
     * arrives with the connection, and a value captured at mount stays empty.
     */
    const localParticipantRef = useRef(localParticipant);
    localParticipantRef.current = localParticipant;
    const readIdentity = useCallback(
        () => room?.localParticipant?.identity || localParticipantRef.current?.identity || "",
        [room]
    );
    const canControl = mayControlToolBoard(readIdentity());

    const [received, setReceived] = useState<PatientToolBoard | null>(null);
    /*
     * Taking the board away waits for the screen to leave fullscreen first
     * (see `leaveBoardFullscreen`). Counted, so a board put up again while
     * that is under way is not taken away by the wait that finishes after it.
     */
    const generationRef = useRef(0);
    const dismiss = useCallback(() => {
        const generation = ++generationRef.current;
        void leaveBoardFullscreen().then(() => {
            if (generationRef.current === generation) setReceived(null);
        });
    }, []);

    const dataSendRef = useRef<DataSend | null>(null);
    const roomRef = useRef(room);
    useEffect(() => {
        roomRef.current = room;
    }, [room]);
    const boardRef = useRef(therapistBoard);
    boardRef.current = therapistBoard;

    const send = useCallback((message: ToolBoardMessage) => {
        const dataSend = dataSendRef.current;
        if (!dataSend) return;
        /*
         * Nothing goes out before the room is connected: LiveKit caches a
         * failed first attempt to open its data transport, and every packet
         * after it would be refused for the rest of the call. Whatever is
         * skipped here is sent once the room connects.
         */
        if (roomRef.current?.state !== ConnectionState.Connected) return;
        void Promise.resolve(
            dataSend(encoder.encode(JSON.stringify(message)), { reliable: true, topic: TOOL_BOARD_TOPIC })
        ).catch(() => undefined);
    }, []);

    /** The therapist's current board, as the room should see it. */
    const sendCurrent = useCallback(() => {
        const board = boardRef.current;
        send(
            board
                ? { kind: "open", board: { kind: board.kind, patientPath: board.patientPath } }
                : { kind: "close" }
        );
    }, [send]);

    const onMessage = useCallback(
        (raw: { payload: Uint8Array; from?: { identity: string } }) => {
            const message = parseToolBoardMessage(raw.payload);
            if (!message) return;
            const from = raw.from?.identity ?? "";
            const self = readIdentity();
            if (message.kind === "sync-request") {
                if (mayControlToolBoard(self)) sendCurrent();
                return;
            }
            // Only the therapist puts boards up or takes them down.
            if (!mayControlToolBoard(from) || mayControlToolBoard(self)) return;
            if (message.kind === "open") {
                generationRef.current += 1;
                setReceived(message.board);
            } else {
                dismiss();
            }
        },
        [readIdentity, sendCurrent, dismiss]
    );

    const { send: dataSend } = useDataChannel(TOOL_BOARD_TOPIC, onMessage);
    useEffect(() => {
        dataSendRef.current = dataSend as unknown as DataSend;
    }, [dataSend]);

    // The therapist's board changed: tell the room.
    const patientPath = therapistBoard?.patientPath ?? null;
    useEffect(() => {
        if (canControl) sendCurrent();
    }, [canControl, patientPath, sendCurrent]);

    /*
     * Put both sides in step once the room is connected: the therapist says
     * what is up, the patient asks — so whichever of the two arrived last,
     * the patient ends up looking at the right thing.
     */
    useEffect(() => {
        if (!room) return;
        const announceWhenReady = () => {
            const self = readIdentity();
            if (room.state !== ConnectionState.Connected || !self) return;
            if (mayControlToolBoard(self)) sendCurrent();
            else send({ kind: "sync-request" });
        };
        announceWhenReady();
        room.on(RoomEvent.Connected, announceWhenReady);
        room.on(RoomEvent.ConnectionStateChanged, announceWhenReady);
        room.on(RoomEvent.Reconnected, announceWhenReady);
        return () => {
            room.off(RoomEvent.Connected, announceWhenReady);
            room.off(RoomEvent.ConnectionStateChanged, announceWhenReady);
            room.off(RoomEvent.Reconnected, announceWhenReady);
        };
    }, [readIdentity, room, send, sendCurrent]);

    if (canControl) {
        return {
            canControl,
            frameSrc: therapistBoard?.therapistPath ?? null,
            kind: therapistBoard?.kind ?? null,
            dismiss,
        };
    }
    // The patient already said who they are when they joined the call.
    const name = room?.localParticipant?.name || localParticipant?.name || "";
    return {
        canControl,
        frameSrc: received ? patientFrameSrc(received.patientPath, name) : null,
        kind: received?.kind ?? null,
        dismiss,
    };
}
